import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type JSX, type ReactNode } from "react";

import { AuthContext } from "./auth-context";
import type { AuthState, AuthUser } from "./auth-context";
import { clearToken, getToken, setToken } from "./token-storage";

/**
 * Callbacks injected by the feature layer so that shared/auth stays free of
 * `api` boundary dependencies.
 *
 * - `verifyToken`: call GET /v1/auth/me and return the user, or throw/return
 *   null on failure.
 * - `postLogout`: fire-and-forget POST /v1/auth/logout (server session
 *   invalidation). Errors are silently swallowed by the caller.
 */
export interface AuthProviderProps {
  children: ReactNode;
  verifyToken: () => Promise<AuthUser | null>;
  postLogout: () => Promise<void>;
}

export function AuthProvider({
  children,
  verifyToken,
  postLogout,
}: AuthProviderProps): JSX.Element {
  const [state, setState] = useState<AuthState>(() => {
    const token = getToken();
    return {
      token,
      user: null,
      isAuthenticated: false,
      status: token ? "pending" : "unauthenticated",
    };
  });
  const qc = useQueryClient();

  // On mount, if we have a token, verify it is still valid via /v1/auth/me.
  useEffect(() => {
    if (!state.token) return;
    let cancelled = false;
    void (async () => {
      try {
        const user = await verifyToken();
        if (cancelled) return;
        if (!user) {
          clearToken();
          setState({ token: null, user: null, isAuthenticated: false, status: "unauthenticated" });
        } else {
          setState((prev) => ({ ...prev, user, isAuthenticated: true, status: "authenticated" }));
        }
      } catch {
        if (cancelled) return;
        clearToken();
        setState({ token: null, user: null, isAuthenticated: false, status: "unauthenticated" });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function login(token: string, expiresAt: string, user: AuthUser): void {
    setToken(token, expiresAt);
    setState({ token, user, isAuthenticated: true, status: "authenticated" });
  }

  function logout(): void {
    clearToken();
    qc.clear();
    setState({ token: null, user: null, isAuthenticated: false, status: "unauthenticated" });
    // Fire-and-forget: invalidate the server-side JWT (add JTI to denylist).
    // Schema confirms POST /v1/auth/logout (verified against src/api/schema.d.ts).
    void postLogout().catch(() => null);
  }

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>{children}</AuthContext.Provider>
  );
}
